// Coucou product detail page — ported from webflow-export/detail_product.html
//
// Drives the Webflow `sm-*` PDP markup while leaning on Horizon's built-ins:
//  - Add-to-cart stays native: the form lives inside <product-form-component>,
//    which AJAX-posts to /cart/add.js and auto-opens the cart drawer.
//  - This file only handles the presentational behaviour Smootify used to do:
//    variant selection (client-side match), quantity stepper, the mobile media
//    slider, the thumbnail strip, and the recommendations carousel.
//  - Requires Swiper v8 (loaded globally via snippets/swiper-scripts.liquid).

(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  const money = (cents) => cents; // prices come pre-formatted from Liquid

  /* ------------------------------------------------------- variant selection */

  class CoucouVariantForm {
    constructor(root) {
      this.root = root;
      const dataEl = root.querySelector('[data-coucou-variants]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[coucou-product] could not parse variants JSON', e);
        return;
      }

      this.optionCount = this.variants.length ? this.variants[0].options.length : 0;
      this.idInput = root.querySelector('input[name="id"]');
      this.priceEl = root.querySelector('[data-coucou-price]');
      this.buyButton = root.querySelector('[data-coucou-buy-button]');
      this.buyLabel = root.querySelector('[data-coucou-buy-label]');
      this.gallery = root.closest('.sm-product-page')?.querySelector('[data-coucou-gallery]') || null;
      this.defaultBuyText = this.buyLabel ? this.buyLabel.textContent.trim() : "It's a match";

      // current selection, indexed by option position (0-based)
      this.selected = [];
      root.querySelectorAll('.sm-variant-button.is-active').forEach((btn) => {
        const pos = Number(btn.dataset.optionPosition) - 1;
        this.selected[pos] = btn.dataset.value;
      });

      // Auto-select first variant when no option buttons exist (all options single-value)
      if (!this.selected.length && this.variants.length) {
        this.variants[0].options.forEach((opt, i) => { this.selected[i] = opt; });
      }

      this.bind();
      this.refresh();
    }

    bind() {
      this.root.querySelectorAll('.sm-variant-button').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (btn.classList.contains('is-disabled')) return;
          const pos = Number(btn.dataset.optionPosition) - 1;
          this.selected[pos] = btn.dataset.value;
          // toggle active state within this option group
          this.root
            .querySelectorAll(`.sm-variant-button[data-option-position="${btn.dataset.optionPosition}"]`)
            .forEach((b) => b.classList.toggle('is-active', b === btn));
          this.refresh();
        });
      });
    }

    currentVariant() {
      return this.variants.find((v) =>
        v.options.every((opt, i) => opt === this.selected[i])
      );
    }

    // A value is buyable if some available variant matches it together with the
    // currently-selected values of the *other* options.
    isValueAvailable(position, value) {
      return this.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[position] !== value) return false;
        return v.options.every((opt, i) => i === position || !this.selected[i] || opt === this.selected[i]);
      });
    }

    refresh() {
      // grey out impossible / sold-out option values
      this.root.querySelectorAll('.sm-variant-button').forEach((btn) => {
        const pos = Number(btn.dataset.optionPosition) - 1;
        const ok = this.isValueAvailable(pos, btn.dataset.value);
        btn.classList.toggle('is-disabled', !ok);
      });

      const variant = this.currentVariant();

      // hidden id input (read by <product-form-component> at submit)
      if (this.idInput && variant) this.idInput.value = variant.id;

      // price
      if (this.priceEl && variant) {
        const priceSpan = this.priceEl.querySelector('.sm-price');
        const compareSpan = this.priceEl.querySelector('.sm-compare-at-price');
        if (priceSpan) priceSpan.textContent = variant.price;
        if (compareSpan) {
          if (variant.compare_at) {
            compareSpan.textContent = variant.compare_at;
            compareSpan.hidden = false;
          } else {
            compareSpan.hidden = true;
          }
        }
      }

      // buy button availability
      if (this.buyButton) {
        const buyable = Boolean(variant && variant.available);
        this.buyButton.disabled = !buyable;
        if (this.buyLabel) {
          this.buyLabel.textContent = !variant
            ? 'Unavailable'
            : variant.available
            ? this.defaultBuyText
            : 'Sold out';
        }
      }

      // sync the shareable ?variant= url
      if (variant && this.root.dataset.coucouUpdateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // move the gallery to the variant's image
      if (variant && variant.media_index != null && this.gallery && this.gallery.swiper) {
        this.gallery.swiper.slideTo(variant.media_index);
      }
    }
  }

  /* ----------------------------------------------------------- quantity step */

  function initQuantity(root) {
    const field = root.querySelector('.sm-quantity-field');
    if (!field) return;
    const minus = root.querySelector('[data-action="minus"]');
    const plus = root.querySelector('[data-action="plus"]');

    const clamp = () => {
      let n = parseInt(field.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      field.value = n;
      if (minus) minus.classList.toggle('is-disabled', n <= 1);
    };

    if (minus)
      minus.addEventListener('click', (e) => {
        e.preventDefault();
        field.value = Math.max(1, (parseInt(field.value, 10) || 1) - 1);
        clamp();
      });
    if (plus)
      plus.addEventListener('click', (e) => {
        e.preventDefault();
        field.value = (parseInt(field.value, 10) || 1) + 1;
        clamp();
      });
    field.addEventListener('change', clamp);
    clamp();
  }

  /* ------------------------------------------------------ buy-button loading */

  function initBuyLoadingState(root) {
    const form = root.matches('form') ? root : root.querySelector('form');
    if (!form) return;
    form.addEventListener('submit', () => {
      const def = root.querySelector('.default-state');
      const load = root.querySelector('.loading-state');
      if (def) def.classList.add('is-hidden');
      if (load) load.classList.remove('is-hidden');
      // restore if the add fails / drawer reopens us later
      setTimeout(() => {
        if (def) def.classList.remove('is-hidden');
        if (load) load.classList.add('is-hidden');
      }, 4000);
    });
  }

  /* ---------------------------------------------------------- media slider */

  function initGallerySlider(root) {
    const el = root.querySelector('[data-coucou-gallery-swiper]');
    if (!el || typeof Swiper === 'undefined') return;
    const wrapper = el.closest('[data-coucou-gallery]') || el;

    const swiper = new Swiper(el, {
      speed: 400,
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      autoHeight: true,
      navigation: {
        prevEl: el.querySelector('.w-slider-arrow-left'),
        nextEl: el.querySelector('.w-slider-arrow-right'),
      },
    });
    wrapper.swiper = swiper;

    // thumbnails jump the slider (mobile) and scroll the stacked list (desktop)
    root.querySelectorAll('[data-coucou-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const i = Number(thumb.dataset.coucouThumb);
        swiper.slideTo(i);
        const stacked = root.querySelectorAll('.sm-product-page__images-list .sm-product__image');
        if (stacked[i]) stacked[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  /* ------------------------------------------------ recommendations carousel */

  function initRecommendationsSlider(el) {
    if (typeof Swiper === 'undefined') return;
    const swiperEl = el.querySelector('.swiper');
    if (!swiperEl || swiperEl.dataset.coucouInit === 'true') return;
    if (!swiperEl.querySelector('.swiper-slide')) return;
    swiperEl.dataset.coucouInit = 'true';

    new Swiper(swiperEl, {
      speed: 400,
      slidesPerView: 'auto',
      spaceBetween: 16,
      freeMode: false,
      followFinger: true,
      grabCursor: true,
      mousewheel: { forceToAxis: true },
      pagination: {
        el: el.querySelector('.slider_bullet_wrap'),
        bulletClass: 'slider_bullet_item',
        bulletActiveClass: 'is-active',
        clickable: true,
      },
      scrollbar: {
        el: el.querySelector('.slider_draggable_wrap'),
        dragClass: 'slider_draggable_handle',
        draggable: true,
      },
    });
  }

  function watchRecommendations() {
    document.querySelectorAll('[data-coucou-recommendations]').forEach((el) => {
      // already populated?
      if (el.querySelector('.swiper-slide')) {
        initRecommendationsSlider(el);
        return;
      }
      // <product-recommendations> injects async after fetch — wait for it
      const observer = new MutationObserver(() => {
        if (el.querySelector('.swiper-slide')) {
          observer.disconnect();
          initRecommendationsSlider(el);
        }
      });
      observer.observe(el, { childList: true, subtree: true });
    });
  }

  /* ---------------------------------------- open cart drawer on add (on-add) */
  // Webflow's buy button used data-open="on-add". Horizon only auto-opens the
  // drawer when the theme setting is enabled, so mirror the design here: when
  // the native product form reports a successful add, open the existing drawer.
  function openDrawerOnAdd() {
    document.addEventListener('cart:update', (event) => {
      const source = event.detail && event.detail.data && event.detail.data.source;
      if (source !== 'product-form-component') return;
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) {
        drawer.open();
      }
    });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    document.querySelectorAll('[data-coucou-product-form]').forEach((root) => {
      new CoucouVariantForm(root);
      initQuantity(root);
      initBuyLoadingState(root);
    });
    document.querySelectorAll('[data-coucou-gallery]').forEach(initGallerySlider);
    watchRecommendations();
    openDrawerOnAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
